import { themeVar } from '@/styles/theme';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { ExportDataIcon } from '@/assets/icons/exportDataIcon';
import { FilterIcon } from '@/assets/icons/filter';
import { FilterSettingIcon } from '@/assets/icons/filterSetting';
import { CloseInfoIcon } from '@/assets/icons/info-icons/closeInfo';
import { RefreshIcon } from '@/assets/icons/info-icons/refresh';
import { SelectIcon } from '@/assets/icons/selectIcon';
import FilterBar, { IFilterBarData } from '@/components/basicComponents/filterBar';
import Input from '@/components/basicComponents/input';
import Search from '@/components/basicComponents/search';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { IToggle, Toggle } from '@/components/basicComponents/toggle';
import SelectWithCheckBox, { TData } from '../../basicComponents/selectWithCheckBox';
import ChosenFilters from './chosenFilters';
import { FilterBarWrap, FilterSettingWrap, QuickFilterSelect, QuickFilterTitle, QuickFilterWrapper, RefreshButton, Results } from './desktop-filter';
import MoreFilters from './moreFilters';
import { AdditionalFilters, IFilterDownloadButton, IQuickFilter } from './types';

interface FilterPageProps {
  search?: {
    value: string;
    onChange: any;
    placeholder: string;
  };
  primaryFilter?: {
    value: string;
    onChange: (value: string) => void;
    selects: string[];
    withSearch?: boolean;
  };
  withCheckBox?: boolean;
  additionalFilters?: AdditionalFilters;
  setAllocationStatusFilter?: any;
  allocationStatusFilter?: TData[];
  currentTab?: { value: string };
  isResult?: boolean;
  resultsValue?: number;
  refetch?: any;
  setIsMoreFiltersOpen?: Dispatch<SetStateAction<boolean>>;
  handleTableColumns?: () => void;
  filterBar?: IFilterBarData[];
  onChangeFilterBar?: (filterBarItems: IFilterBarData[]) => void;
  mobileSort?: {
    value: string;
    onChange: (value: string) => void;
    selects: string[];
  };
  viewBySelect?: {
    value: string;
    onChange: (value: string) => void;
    selects: string[];
  };
  useNewFilters?: boolean;
  advisoryFirmQuickFilter?: IQuickFilter;
  viewByQuickFilter?: IQuickFilter;
  quickFilter?: IQuickFilter;
  toggle?: IToggle;
  downloadButton?: IFilterDownloadButton;
}

const PortraitFilterPage = ({
  search,
  primaryFilter,
  additionalFilters,
  withCheckBox,
  setAllocationStatusFilter,
  allocationStatusFilter,
  currentTab,
  isResult,
  resultsValue,
  refetch,
  setIsMoreFiltersOpen,
  handleTableColumns,
  filterBar,
  onChangeFilterBar,
  mobileSort,
  viewBySelect,
  useNewFilters = false,
  advisoryFirmQuickFilter,
  viewByQuickFilter,
  quickFilter,
  toggle,
  downloadButton
}: FilterPageProps) => {
  const [moreFilter, setMoreFilter] = useState(false);
  const [chosenFilters, setChosenFilters] = useState(!!additionalFilters?.showChosenFilters);
  const [checkBoxData, setCheckBoxData] = useState<TData[]>(allocationStatusFilter ?? []);
  useEffect(() => {
    if (additionalFilters?.filters[0].typeFilter === 'Select A Filter Option' && currentTab) {
      setChosenFilters(false);
    }
  }, [currentTab?.value, additionalFilters]);

  useEffect(() => {
    if (additionalFilters?.filters[0].typeFilter !== 'Select A Filter Option') {
      setChosenFilters(true);
    }
  }, []);

  const checkBoxSelectLabel = useMemo(() => {
    const choosenNames = checkBoxData.filter((el) => el.value);
    let label = '';
    if (choosenNames.length >= 1 && choosenNames.length <= 2) {
      choosenNames.map((el, index) => {
        if (index === 0) {
          label = el.name;
        } else {
          label = label + ', ' + el.name;
        }
      });
      return label;
    } else {
      return choosenNames.length === 0 ? 'Filter by' : choosenNames.length + ' ' + 'Selected';
    }
  }, [checkBoxData]);

  const handleCheckBoxData = (id: number) => {
    const newData = [...checkBoxData];
    newData[id].value = !newData[id].value;
    setCheckBoxData(newData);
  };

  const openMoreFilters = () => {
    setMoreFilter(!moreFilter);
    if (setIsMoreFiltersOpen) {
      setIsMoreFiltersOpen((prev) => !prev);
    }
  };

  const closeMoreFilters = () => {
    setMoreFilter(false);
    if (setIsMoreFiltersOpen) {
      setIsMoreFiltersOpen(false);
    }
  };

  if (useNewFilters) {
    return (
      <FiltersWrapper>
        {filterBar && (
          <FilterBarWrap>
            <FilterBar filterBar={filterBar} onChangeFilterBar={onChangeFilterBar} />
          </FilterBarWrap>
        )}
        {(search || additionalFilters || viewByQuickFilter || advisoryFirmQuickFilter || quickFilter || downloadButton) && (
          <TableToolbar>
            {(search || additionalFilters) && (
              <SearchWrapper>
                {search && <Search value={search.value} onChange={search.onChange} placeholder={search.placeholder} width="100%" />}
                {additionalFilters && (
                  <FilterWrap onClick={openMoreFilters} moreFilter={moreFilter}>
                    {moreFilter ? (
                      <CloseInfoIcon fill={themeVar('context.light')} width={11} height={11} />
                    ) : (
                      <FilterIcon fill={themeVar('font.action')} />
                    )}
                  </FilterWrap>
                )}
                {handleTableColumns && (
                  <FilterSettingWrap onClick={handleTableColumns}>
                    <FilterSettingIcon fill={themeVar('font.action')} />
                  </FilterSettingWrap>
                )}
              </SearchWrapper>
            )}
            {(viewByQuickFilter || advisoryFirmQuickFilter || quickFilter || downloadButton) && (
              <Filters>
                {viewByQuickFilter && (
                  <QuickFilterWrapper>
                    <QuickFilterTitle>{viewByQuickFilter.title}</QuickFilterTitle>
                    <QuickFilterSelect
                      value={viewByQuickFilter.value}
                      setValue={viewByQuickFilter.onChange}
                      defaultSelectLabel={viewByQuickFilter.defaultValue}
                      options={viewByQuickFilter.options}
                      size="sm"
                      customArrowIcon={<SelectIcon fill={themeVar('font.action')} />}
                    />
                  </QuickFilterWrapper>
                )}
                {advisoryFirmQuickFilter && (
                  <QuickFilterWrapper>
                    <QuickFilterTitle>{advisoryFirmQuickFilter.title}</QuickFilterTitle>
                    <QuickFilterSelect
                      value={advisoryFirmQuickFilter.value}
                      setValue={advisoryFirmQuickFilter.onChange}
                      defaultSelectLabel={advisoryFirmQuickFilter.defaultValue}
                      options={advisoryFirmQuickFilter.options}
                      centerList={advisoryFirmQuickFilter?.centerList}
                      size="sm"
                      customArrowIcon={<SelectIcon fill={themeVar('font.action')} />}
                    />
                  </QuickFilterWrapper>
                )}
                {quickFilter && (
                  <QuickFilterWrapper>
                    <QuickFilterTitle>{quickFilter.title}</QuickFilterTitle>
                    <QuickFilterSelect
                      value={quickFilter.value}
                      setValue={quickFilter.onChange}
                      defaultSelectLabel={quickFilter.defaultValue}
                      options={quickFilter.options}
                      size="sm"
                      customArrowIcon={<SelectIcon fill={themeVar('font.action')} />}
                    />
                  </QuickFilterWrapper>
                )}
                {downloadButton && (
                  <DownloadButtonWrapper onClick={downloadButton.onClick}>
                    <div>
                      <ExportDataIcon fill={themeVar('font.action')} />
                    </div>
                    <DownloadButtonTitle>{downloadButton?.title || 'Export Data'}</DownloadButtonTitle>
                  </DownloadButtonWrapper>
                )}
              </Filters>
            )}
          </TableToolbar>
        )}

        {additionalFilters && moreFilter && <MoreFilters onClose={closeMoreFilters} setChosenFilters={setChosenFilters} {...additionalFilters} />}
        {additionalFilters && chosenFilters && <ChosenFilters setChosenFilters={setChosenFilters} {...additionalFilters} />}

        {mobileSort && (
          <>
            <MobileSortSelectWrapper>
              <Select
                value={mobileSort.value}
                setValue={mobileSort.onChange}
                options={mobileSort.selects.map((item) => createSimpleSelectOption(item))}
                withSearch={!!primaryFilter?.withSearch}
                label="Sort By"
                size="sm"
              />
            </MobileSortSelectWrapper>
            <TopWrapper>
              <Results>{additionalFilters?.amountResults || resultsValue || 0} Results</Results>
              <RefreshButton
                onClick={() => {
                  if (additionalFilters?.onRefetch) {
                    additionalFilters.onRefetch();
                  } else if (refetch) {
                    refetch();
                  } else null;
                }}
              >
                <RefreshIcon fill={themeVar('action.primary')} />
              </RefreshButton>

              {toggle && (
                <ToggleWrapper>
                  <Toggle title={toggle.title} isActive={toggle.isActive} onToggle={toggle.onToggle} />
                </ToggleWrapper>
              )}
            </TopWrapper>
          </>
        )}
      </FiltersWrapper>
    );
  }

  return (
    <>
      <DefaultSearchWrap>
        <MainSearchWrap>
          {downloadButton && (
            <DownloadButtonWrapper onClick={downloadButton.onClick}>
              <ExportDataIcon fill={themeVar('font.action')} />
              <DownloadButtonTitle>{downloadButton?.title || 'Export Data'}</DownloadButtonTitle>
            </DownloadButtonWrapper>
          )}
          <SearchAdditionalWrap isCheckBoxPrimaryFilter={Boolean((currentTab?.value === 'pending' && withCheckBox) || primaryFilter)}>
            <InputWrapper width={additionalFilters ? '96%' : '100%'}>
              <Input
                value={search.value}
                onChange={(e) => search.onChange(e.target.value)}
                placeholder={search.placeholder}
                size="sm"
                withSearchIcon
              />
            </InputWrapper>
            {additionalFilters && (
              <FilterWrap onClick={openMoreFilters} moreFilter={moreFilter}>
                <FilterIcon fill={moreFilter ? themeVar('context.light') : themeVar('font.action')} />
              </FilterWrap>
            )}
            {handleTableColumns && (
              <FilterSettingWrap onClick={handleTableColumns}>
                <FilterSettingIcon fill={themeVar('font.action')} />
              </FilterSettingWrap>
            )}
          </SearchAdditionalWrap>
          {primaryFilter && (
            <Select
              value={primaryFilter.value}
              setValue={primaryFilter.onChange}
              options={primaryFilter.selects.map((item) => createSimpleSelectOption(item))}
              withSearch={!!primaryFilter?.withSearch}
              size="sm"
            />
          )}
          {currentTab?.value === 'pending' && withCheckBox && (
            <SelectWithCheckBox
              width={'100%'}
              data={checkBoxData}
              setSelected={handleCheckBoxData}
              label={checkBoxSelectLabel}
              setAllocationStatusFilter={setAllocationStatusFilter}
              setCheckBoxData={setCheckBoxData}
            />
          )}
        </MainSearchWrap>
      </DefaultSearchWrap>
      {additionalFilters && moreFilter && <MoreFilters onClose={closeMoreFilters} setChosenFilters={setChosenFilters} {...additionalFilters} />}
      {additionalFilters && chosenFilters && <ChosenFilters setChosenFilters={setChosenFilters} {...additionalFilters} />}
      {filterBar && (
        <FilterBarWrap>
          <FilterBar filterBar={filterBar} onChangeFilterBar={onChangeFilterBar} />
        </FilterBarWrap>
      )}
      {mobileSort && (
        <>
          <MobileSortSelectWrapper>
            <Select
              value={mobileSort.value}
              setValue={mobileSort.onChange}
              options={mobileSort.selects.map((item) => createSimpleSelectOption(item))}
              withSearch={!!primaryFilter?.withSearch}
              label="Sort By"
              size="sm"
            />
          </MobileSortSelectWrapper>
          <TopWrapper>
            <Results>{additionalFilters?.amountResults || resultsValue || 0} Results</Results>
            <RefreshButton
              onClick={() => {
                if (additionalFilters?.onRefetch) {
                  additionalFilters.onRefetch();
                } else if (refetch) {
                  refetch();
                } else null;
              }}
            >
              <RefreshIcon fill={themeVar('action.primary')} />
            </RefreshButton>
            {viewBySelect && (
              <QuickFilterWrapper padding="0 10px 0 30px">
                <QuickFilterTitle>View By</QuickFilterTitle>
                <QuickFilterSelect
                  value={viewBySelect.value}
                  setValue={viewBySelect.onChange}
                  options={viewBySelect.selects.map((item) => createSimpleSelectOption(item))}
                  centerList={true}
                  size="sm"
                  customArrowIcon={<SelectIcon fill={themeVar('font.action')} />}
                />
              </QuickFilterWrapper>
            )}
          </TopWrapper>
        </>
      )}
    </>
  );
};

export default PortraitFilterPage;

const SearchAdditionalWrap = styled.div<{ isCheckBoxPrimaryFilter: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: ${({ isCheckBoxPrimaryFilter }) => (isCheckBoxPrimaryFilter ? '10px' : '0')};
`;

const DownloadButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  gap: 6px;
  padding: 5px 20px;
`;

const DownloadButtonTitle = styled.p`
  font-family: Blinker, serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 18.2px;
  white-space: nowrap;
  color: ${themeVar('font.action')};
`;

const FilterWrap = styled.div<{ moreFilter: boolean }>`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ moreFilter }) => (moreFilter ? themeVar('action.default') : themeVar('layer.base'))};
  border: ${({ moreFilter }) => `1px solid ${moreFilter ? themeVar('action.default') : themeVar('font.action')}`};
  border-radius: 3px;
  padding: 10px;
`;

const DefaultSearchWrap = styled.div`
  //width: 100%;
  //display: flex;
  //align-items: flex-end;
  //justify-content: flex-end;
  //gap: 10px;
`;

const MainSearchWrap = styled.div`
  padding: 8px 20px;
`;

const InputWrapper = styled.div<{ width: string }>`
  width: ${({ width }) => width};
`;

const TopWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 10px;
  margin-top: 10px;
`;

const ToggleWrapper = styled.div`
  margin-left: auto;
`;

export const ViewByWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5px 10px 5px 30px;
  gap: 6px;
`;

const TableToolbar = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const FiltersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Filters = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  padding: 0 20px;
`;

const MobileSortSelectWrapper = styled.div`
  padding: 0 20px;
`;
